import { useState } from 'react';
import {
  Stack, Group, Card, Text, Button, NumberInput, TextInput, SegmentedControl,
  Select, Alert, Box, Center, Loader, Divider, Switch,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import QRCode from 'qrcode';
import { Printer, QrCode as QrIcon } from 'lucide-react';

import { Eyebrow } from '../components/Eyebrow';
import { parseNumberList, renderMemberQr } from '../utils/qr';
import { plural } from '../utils/format';

/** Kreslení stovek kódů není zadarmo — nad tímhle už by to prohlížeč odnesl. */
const MAX_CODES = 300;

interface Tile {
  number: number;
  dataUrl: string;
}

/**
 * Generátor je schválně nezávislý na evidenci členů — čísla si zadává uživatel,
 * ať si může kódy vytisknout dopředu, ještě než někoho do epochy založí.
 */
export default function QrCodesPage() {
  const [mode, setMode] = useState<'range' | 'list'>('range');
  const [from, setFrom] = useState<string | number>(1);
  const [to, setTo] = useState<string | number>(20);
  const [list, setList] = useState('1, 4, 7-9');
  const [columns, setColumns] = useState('4');
  // Štítky se laminují a děrují na provázek — nad kódem musí zůstat prázdno.
  const [holePunch, setHolePunch] = useState(false);
  const [holeMm, setHoleMm] = useState<string | number>(12);

  const [tiles, setTiles] = useState<Tile[]>([]);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const collectNumbers = (): { numbers: number[]; errors: string[] } => {
    if (mode === 'list') return parseNumberList(list);

    const a = Number(from);
    const b = to === '' || to === null ? a : Number(to);
    if (!Number.isFinite(a) || a < 1) return { numbers: [], errors: ['Od: zadej číslo od 1'] };
    if (!Number.isFinite(b) || b < 1) return { numbers: [], errors: ['Do: zadej číslo od 1'] };
    if (b < a) return { numbers: [], errors: ['Do je menší než Od'] };

    const numbers: number[] = [];
    for (let n = a; n <= b; n++) numbers.push(n);
    return { numbers, errors: [] };
  };

  const generate = async () => {
    const { numbers, errors } = collectNumbers();
    if (numbers.length === 0) {
      setProblems(errors.length ? errors : ['Zadej aspoň jedno číslo.']);
      setTiles([]);
      return;
    }
    if (numbers.length > MAX_CODES) {
      setProblems([`Najednou zvládneme ${MAX_CODES} kódů, zadáno je ${numbers.length}. Rozděl to.`]);
      return;
    }

    setBusy(true);
    setProblems(errors);
    try {
      // Rozlišení je pevné a vysoké kvůli tisku; velikost na stránce řeší CSS.
      const rendered = await Promise.all(
        numbers.map(async (n) => ({ number: n, dataUrl: await renderMemberQr(n, 512, QRCode) }))
      );
      setTiles(rendered);
    } catch (e: any) {
      notifications.show({ title: 'Kódy se nepodařilo vytvořit', message: e.message, color: 'red' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack gap="sm">
      <Card className="qr-controls">
        <Stack gap="md">
          <Box>
            <Eyebrow>Která čísla</Eyebrow>
            <SegmentedControl
              mt={6}
              value={mode}
              onChange={(v) => { setMode(v as 'range' | 'list'); setProblems([]); }}
              data={[{ value: 'range', label: 'Rozsah' }, { value: 'list', label: 'Výčet' }]}
            />

            {mode === 'range' ? (
              <Group gap="sm" mt="sm" align="flex-start" wrap="nowrap">
                <NumberInput label="Od" w={120} min={1} value={from} onChange={setFrom} allowDecimal={false} />
                <NumberInput label="Do" w={120} min={1} value={to} onChange={setTo} allowDecimal={false} />
              </Group>
            ) : (
              <TextInput
                mt="sm"
                label="Čísla"
                value={list}
                onChange={(e) => setList(e.currentTarget.value)}
                placeholder="1, 4, 7-9"
              />
            )}

            <Text c="dimmed" fz="xs" mt={6}>
              {mode === 'range'
                ? 'Prázdné „Do“ vygeneruje jediné číslo.'
                : 'Odděluj čárkou. Rozsah se píše pomlčkou, třeba 7-9.'}
            </Text>
          </Box>

          <Divider />

          <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
            <Select
              label="Sloupců na stránku"
              description="Víc sloupců = menší kódy"
              w={190}
              data={['3', '4', '5', '6']}
              value={columns}
              onChange={(v) => setColumns(v ?? '4')}
              allowDeselect={false}
            />
            <Group gap="sm" align="flex-end">
              <Switch
                label="Místo na dírku"
                description="Prázdno nad kódem pro děrovačku"
                checked={holePunch}
                onChange={(e) => setHolePunch(e.currentTarget.checked)}
                mb={6}
              />
              {holePunch && (
                <NumberInput
                  label="Výška (mm)" w={110} min={4} max={40} step={2}
                  value={holeMm} onChange={setHoleMm} allowDecimal={false}
                />
              )}
            </Group>
            <Group gap="sm">
              <Button
                variant="default" leftSection={<Printer size={14} />}
                disabled={tiles.length === 0} onClick={() => window.print()}
              >
                Vytisknout
              </Button>
              <Button leftSection={<QrIcon size={14} />} loading={busy} onClick={generate}>
                Vygenerovat
              </Button>
            </Group>
          </Group>

          {problems.length > 0 && (
            <Alert color="orange" title="Zadání">
              <Stack gap={2}>{problems.map((p) => <Text key={p} fz="sm">{p}</Text>)}</Stack>
            </Alert>
          )}
        </Stack>
      </Card>

      {busy && <Center h={160}><Loader /></Center>}

      {!busy && tiles.length > 0 && (
        <>
          <Group className="qr-controls">
            <Text fw={700} fz="sm">{plural(tiles.length, 'kód', 'kódy', 'kódů')}</Text>
          </Group>

          <Box
            className="qr-sheet"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: 10,
              // Milimetry, ať prázdno sedí po vytištění, ne jen na obrazovce.
              ['--qr-hole' as string]: holePunch ? `${Number(holeMm) || 12}mm` : undefined,
            } as React.CSSProperties}
          >
            {tiles.map((t) => (
              <Box key={t.number} className="qr-cell">
                {/* Číslo je vykreslené uvnitř kódu, popisek pod ním by ho jen opakoval. */}
                <img src={t.dataUrl} alt={`QR kód pro číslo ${t.number}`} />
              </Box>
            ))}
          </Box>
        </>
      )}

      {!busy && tiles.length === 0 && (
        <Card className="qr-controls">
          <Stack align="center" py="lg" gap={4}>
            <QrIcon size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Zatím žádné kódy</Text>
            <Text c="dimmed" fz="sm">Zadej rozsah nebo výčet čísel a dej Vygenerovat.</Text>
          </Stack>
        </Card>
      )}

      {tiles.length > 0 && (
        <Eyebrow>Tisk vynechá menu i ovládání — na stránce zůstane jen mřížka kódů.</Eyebrow>
      )}
    </Stack>
  );
}
